import React, { useState } from 'react';
import axios from 'axios';
import { HelpCircle, MessageSquare, BookOpen, Mail, Send, ExternalLink, Loader2, CheckCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const GlassCard = ({ children, className = '', ...props }) => (
  <div className={`bg-[#171A2C]/60 backdrop-blur-xl border border-white/5 shadow-[0_8px_32px_0_rgba(0,0,0,0.36)] rounded-2xl ${className}`} {...props}>{children}</div>
);

export default function DashboardHelp() {
  const [form, setForm] = useState({ subject: '', message: '', priority: 'normal' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject || !form.message) {
      toast.error('Please fill in subject and message');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/support/ticket`, form, { headers });
      toast.success('Support ticket submitted!');
      setSent(true);
      setForm({ subject: '', message: '', priority: 'normal' });
    } catch (error) {
      toast.error('Failed to submit ticket');
    } finally {
      setSending(false);
    }
  };

  const faqs = [
    { q: 'How do I add products to my store?', a: 'Go to Products > Add New Product. Upload images via drag & drop, set pricing, sizes, colors, and categories.' },
    { q: 'How do I customize my storefront?', a: 'Use the Content Editor to add/edit blocks. Changes preview live. Use Settings > Theme Colors to change your color scheme.' },
    { q: 'How do I manage orders?', a: 'Go to Orders to view all orders. Filter by status (Pending/Fulfilled). Click an order to update status or add tracking numbers.' },
    { q: 'How do I set up payments?', a: 'Go to Settings and enter your PayPal Client ID or Stripe details. Your customers can pay directly on your store.' },
    { q: 'How do I export my email subscribers?', a: 'Go to Messages and click "Export CSV" to download all your subscribers.' },
    { q: 'How do I connect email marketing?', a: 'In the Messages tab, you\'ll find integration options for Mailchimp, Klaviyo, and Constant Contact.' },
    { q: 'How do I add a custom domain?', a: 'Go to Settings > Domain and follow the instructions to point your domain to our servers.' },
    { q: 'How do I create discount codes?', a: 'Go to Discounts > Add Discount. Set the code, type (percentage or fixed), value, and optional expiry date.' },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-help">
      <div>
        <h1 className="text-2xl font-bold text-white" data-testid="help-title">Help & Support</h1>
        <p className="text-[#9CA3AF] text-sm mt-1">Get help with your store dashboard</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Submit Ticket */}
        <GlassCard className="p-6" data-testid="support-ticket">
          <div className="flex items-center gap-2 mb-5">
            <MessageSquare size={16} className="text-[#A78BFA]" />
            <h2 className="font-semibold text-white">Contact Support</h2>
          </div>
          {sent ? (
            <div className="text-center py-8">
              <CheckCircle size={48} className="mx-auto text-[#00CC99] mb-4" />
              <h3 className="font-semibold text-white mb-2">Ticket Submitted!</h3>
              <p className="text-[#9CA3AF] text-sm mb-4">We'll get back to you as soon as possible.</p>
              <button onClick={() => setSent(false)} className="text-sm text-[#A78BFA] hover:text-white transition-colors">Submit another ticket</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="support-form">
              <div>
                <Label className="text-[#94A3B8] text-sm">Subject</Label>
                <Input value={form.subject} onChange={(e) => setForm(p => ({...p, subject: e.target.value}))} placeholder="What do you need help with?" required className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl" data-testid="ticket-subject" />
              </div>
              <div>
                <Label className="text-[#94A3B8] text-sm">Priority</Label>
                <select value={form.priority} onChange={(e) => setForm(p => ({...p, priority: e.target.value}))} className="w-full mt-1.5 bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2 text-sm" data-testid="ticket-priority">
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div>
                <Label className="text-[#94A3B8] text-sm">Message</Label>
                <Textarea value={form.message} onChange={(e) => setForm(p => ({...p, message: e.target.value}))} placeholder="Describe your issue in detail..." rows={5} required className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl" data-testid="ticket-message" />
              </div>
              <button type="submit" disabled={sending} className="w-full py-3 rounded-xl font-medium text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_15px_rgba(139,92,246,0.3)] hover:shadow-[0_0_25px_rgba(139,92,246,0.5)] disabled:opacity-50 transition-all flex items-center justify-center gap-2" data-testid="submit-ticket-btn">
                <Send size={16} /> {sending ? 'Sending...' : 'Submit Ticket'}
              </button>
            </form>
          )}
        </GlassCard>

        {/* Quick Links */}
        <div className="space-y-5">
          <GlassCard className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Mail size={16} className="text-[#A78BFA]" />
              <h2 className="font-semibold text-white">Direct Contact</h2>
            </div>
            <div className="space-y-3 text-sm">
              <p className="text-[#9CA3AF]">For urgent matters, reach out directly:</p>
              <div className="p-3 bg-white/5 rounded-xl"><p className="text-[#64748B] text-xs">Email</p><p className="text-[#A78BFA]">support@yourbrand.com</p></div>
              <div className="p-3 bg-white/5 rounded-xl"><p className="text-[#64748B] text-xs">Response Time</p><p className="text-white">Within 24 hours</p></div>
            </div>
          </GlassCard>

          <GlassCard className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen size={16} className="text-[#A78BFA]" />
              <h2 className="font-semibold text-white">Quick Start Guides</h2>
            </div>
            <div className="space-y-2">
              {['Getting Started', 'Adding Products', 'Managing Orders', 'Customizing Your Store', 'Email Marketing Setup'].map((guide) => (
                <button key={guide} className="w-full flex items-center justify-between p-3 bg-white/5 rounded-xl text-sm text-[#9CA3AF] hover:text-white hover:bg-white/10 transition-colors text-left">
                  <span>{guide}</span>
                  <ExternalLink size={14} className="text-[#64748B]" />
                </button>
              ))}
            </div>
          </GlassCard>
        </div>
      </div>

      {/* FAQ */}
      <GlassCard className="p-6" data-testid="faq-section">
        <div className="flex items-center gap-2 mb-5">
          <HelpCircle size={16} className="text-[#A78BFA]" />
          <h2 className="font-semibold text-white">Frequently Asked Questions</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {faqs.map((faq, i) => (
            <div key={i} className="p-4 bg-white/[0.03] border border-white/5 rounded-xl hover:border-[#8B5CF6]/20 transition-colors" data-testid={`faq-${i}`}>
              <h3 className="text-sm font-medium text-white mb-2">{faq.q}</h3>
              <p className="text-xs text-[#9CA3AF] leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </GlassCard>
    </div>
  );
}
